import React, { useEffect, useState } from "react";
import { FaTimes, FaFilter } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Toast from "../../components/Toast/Toast";
import { getOrderAPI, updateOrderStatusAPI } from "../../utils/api";
import Sidebar from "../Sidebar/Sidebar";
import "./Order.css";
import { LiaRupeeSignSolid } from "react-icons/lia";

const statusMap = {
  processing: { label: "Processing", color: "#FF9800" },
  delivered: { label: "Delivered", color: "#17BF52" },
  ordered: { label: "Ordered", color: "#0A727E" },
  cancelled: { label: "Cancelled", color: "red" }
};

const paymentStatusMap = {
  1: { label: "Paid" },
  0: { label: "Unpaid" },
};

const userTypeMap = {
  wholesaler: "Wholesaler",
  retailer: "Retailer"
};

const Order = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [orderFilter, setOrderFilter] = useState(null);
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Payment status modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState("");
  const [paymentUpdateLoading, setPaymentUpdateLoading] = useState(false);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    status: "",
    userType: "",
    search: ""
  });

  // Check if device is mobile/tablet
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get("userId");
    const refresh = queryParams.get("refresh");
    const fromSidebar = queryParams.get("fromSidebar");
    const fromDashboard = queryParams.get("fromDashboard");

    if (userId && !fromSidebar && !fromDashboard) {
      setOrderFilter(userId);
      fetchOrders(userId,filters);
    } else {
      fetchOrders(null,filters);
    }

    if (refresh) {
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('refresh');
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get("userId");
    const fromSidebar = queryParams.get("fromSidebar");
    const fromDashboard = queryParams.get("fromDashboard");

    if (userId && !fromSidebar && !fromDashboard) {
        fetchOrders(userId,filters);
    } else {
      fetchOrders(null,filters);
    }
  }, [currentPage]);

  const fetchOrders = async (userId = null,filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      let requestBody = {
        page: currentPage,
        limit: 10,
        is_report: 0
      };

      if (userId) {
        requestBody.user_id = userId;
      }

      if (filters) {
        if (filters.fromDate) {
          const date = new Date(filters.fromDate);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          requestBody.from_date = `${day}-${month}-${year}`;
        }
        if (filters.toDate) {
          const date = new Date(filters.toDate);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          requestBody.to_date = `${day}-${month}-${year}`;
        }
        if (filters.status) requestBody.status = filters.status;
        if (filters.userType) requestBody.user_type = filters.userType;
        if (filters.search) requestBody.search = filters.search;
        if (filters.paymentStatus) requestBody.paid = filters.paymentStatus;
      }

      const response = await getOrderAPI(requestBody);

      if (response?.status === 200) {
        setOrders(response?.data || []);

        if (response?.pagination) {
          const currentPageFromApi = response.pagination.current_page || currentPage;
          const totalPagesFromApi = response.pagination.total_pages || 1;
          const hasNextPageFromApi = response.pagination.has_next_page || false;

          setCurrentPage(currentPageFromApi);
          setTotalPages(totalPagesFromApi);
          setHasNextPage(hasNextPageFromApi);
        }

      } else if (response?.message) {
        setError(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error');
      } else {
        setError('Failed due to internal error');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed due to internal error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      status: "",
      userType: "",
      search: ""
    });
    setCurrentPage(1);
    fetchOrders(orderFilter, {
      fromDate: "",
      toDate: "",
      status: "",
      userType: "",
      search: ""
    });
  };

  const applyFilters = () => {
    setCurrentPage(1);
    if (isMobile) {
      setShowFilters(false);
    }
    fetchOrders(orderFilter, filters);
  };

  const handleUpdateStatusClick = (order) => {
    if (order.order_status?.toLowerCase() === 'cancelled') {
      return;
    }
    setSelectedOrder(order);
    setNewStatus("");
    setShowModal(true);
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder || !newStatus) {
      console.error("No order selected or no status chosen");
      return;
    }

    try {
      const response = await updateOrderStatusAPI({
        order_id: selectedOrder._id,
        status: newStatus
      });

      if (response?.status === 200) {
        await fetchOrders(orderFilter,filters);
        setShowModal(false);
        setSelectedOrder(null);
        setNewStatus("");
        showToast("Order status updated successfully");
      } else if (response?.message) {
        showToast(response?.message || "Failed to update order status", "error");
      } else {
        showToast("Failed to update order status", "error");
      }
    } catch (err) {
      showToast("Failed to update order status", "error");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
  };

  // Payment status update functions
  const handlePaymentStatusClick = (order, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedOrderForPayment(order);
    setNewPaymentStatus(order.paid?.toString() || "0");
    setShowPaymentModal(true);
  };

  const updatePaymentStatus = async () => {
    if (!selectedOrderForPayment || newPaymentStatus === "") {
      console.log("No order selected or no payment status chosen");
      return;
    }

    try {
      setPaymentUpdateLoading(true);
      const response = await updateOrderStatusAPI({
        order_id: selectedOrderForPayment._id,
        paid: parseInt(newPaymentStatus),
      });

      if (response && response.status === 200) {
        await fetchOrders(orderFilter, filters);
        setShowPaymentModal(false);
        setSelectedOrderForPayment(null);
        setNewPaymentStatus("");
        showToast("Payment status updated successfully");
      } else if (response?.message) {
        showToast(response?.message || "Failed to update payment status", "error");
      } else {
        showToast("Failed to update payment status", "error");
      }
    } catch (err) {
      showToast("Failed to update payment status", "error");
    } finally {
      setPaymentUpdateLoading(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedOrderForPayment(null);
    setNewPaymentStatus("");
  };

  // Determine if filters should be shown
  const shouldShowFilters = !isMobile || showFilters;

  // Helper function to format SKU string
  const formatSKU = (products) => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return "N/A";
    }

    return products.map((product) => {
      const sku = product.sku||"";
      const size = product.size||"";
      const color = product.color||"";

      if (color) {
        return `${sku}_${size}_${color}`;
      } else {
        return `${sku}_${size}_nocolor`;
      }
    }).join(", ");
  };

  // Helper function to format Quantity string
  const formatQuantity = (products) => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return "N/A";
    }

    return products.map((product) => {
      return product.qty || product.quantity || 0;
    }).join("_");
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="Order-layout">
        <Sidebar />
        <div className="Order-main-content">
          {/* Filter Header - Only show on mobile */}
          {isMobile && (
            <div className="filter-header">
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>
          )}

          {/* Filter Section - Always show on desktop, conditional on mobile */}
          {shouldShowFilters && (
            <div className="filter-section">
              <div className="filter-row">
                <div className="filter-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    className="filter-input"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="filter-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    className="filter-input"
                    min={filters.fromDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Status</option>
                    <option value="ordered">Ordered</option>
                    <option value="processing">Processing</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>User Type</label>
                  <select
                    value={filters.userType}
                    onChange={(e) => handleFilterChange('userType', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Users</option>
                    <option value="wholeseller">Wholeseller</option>
                    <option value="retailer">Retailer</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Payment Status</label>
                  <select
                    value={filters.paymentStatus}
                    onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All</option>
                    <option value="1">Paid</option>
                    <option value="0">Unpaid</option>
                  </select>
                </div>
                </div>
                <div className="filter-group search-group">
                  <label>Search</label>
                  <div className="search-row">
                    <input
                      type="text"
                      placeholder="Search by order ID, user name, product etc."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="filter-input"
                    />
                    <div className="filter-actions">
                      <button className="clear-filters-btn" onClick={clearFilters}>
                        Clear All
                      </button>
                      <button className="apply-filters-btn" onClick={applyFilters}>
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          )}

          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading orders...</p>
            </div>
          )}

          {error && (
            <div className="error-container">
              <div style={{ color: "#dc2626", fontSize: "1.25rem", marginBottom: "1rem" }}>⚠️</div>
              <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
            </div>
          )}

          {!loading && !error && (
            orders.length === 0 ? (
              <div className="empty-state">
                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📦</div>
                <h3>No Orders Found</h3>
                <p>{new URLSearchParams(location.search).get("userId") ? 'No orders found for this user.' : 'No orders found.'}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="Order-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>SKU</th>
                      <th>Quantity</th>
                      <th>Total</th>
                      <th>Order Status</th>
                      <th>Payment Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => (
                      <tr
                        key={order._id}
                        className="order-row"
                        onClick={() => navigate(`/order-details/${order._id}`)}
                      >
                        <td>{((currentPage - 1) * 10) + idx + 1}</td>
                        <td>{order.user?.name || order.name}</td>
                        <td>{formatSKU(order.products)}</td>
                        <td>{formatQuantity(order.products)}</td>
                        <td>
                          <span className="total-amount">
                            <LiaRupeeSignSolid className="rupee-icon" />
                            {order.total}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              color: statusMap[order.order_status?.toLowerCase()]?.color
                            }}
                          >
                            {statusMap[order.order_status?.toLowerCase()]?.label}
                          </span>
                        </td>
                        <td className="payment-status-cell" onClick={(e) => handlePaymentStatusClick(order, e)}>
                          {paymentStatusMap[order.paid]?.label &&
                            <span className="payment-status-text">
                              {paymentStatusMap[order.paid]?.label}
                            </span>
                          }
                        </td>
                        <td className="action-cell" onClick={e => e.stopPropagation()}>
                          {order.order_status?.toLowerCase() !== 'delivered' &&
                            order.order_status?.toLowerCase() !== 'cancelled' && (
                              <button className="update-status-btn" onClick={() => handleUpdateStatusClick(order)}>
                                Status
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Pagination */}
          {orders.length > 0 && (
            <div className="table-footer">
              <div className="footer-left">
                <span>
                  Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, ((currentPage - 1) * 10) + orders.length)} of {((currentPage - 1) * 10) + orders.length} entries (Page {currentPage} of {totalPages})
                </span>
              </div>
              <div className="pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="pagination-btn"
                >
                  Previous
                </button>
                {[...Array(totalPages).keys()].slice(0, 5).map((i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
                  >
                    {i + 1}
                  </button>
                ))}
                {totalPages > 5 && <span className="ellipsis">…</span>}
                <button
                  disabled={!hasNextPage}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Status Update Modal */}
          {showModal && selectedOrder && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content add-edit-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Update Order Status</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="profile-form">
                  <div className="form-group">
                    <label className="form-label" htmlFor="status-select">Select Status</label>
                    <div className="dropdown-wrapper">
                      <select
                        id="status-select"
                        className="status-dropdown"
                        value={newStatus}
                        onChange={e => setNewStatus(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="processing">Processing</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={updateOrderStatus}
                      disabled={!newStatus}
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Status Update Modal */}
          {showPaymentModal && selectedOrderForPayment && (
            <div className="modal-overlay" onClick={closePaymentModal}>
              <div className="modal-content add-edit-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Update Payment Status</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={closePaymentModal}
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="profile-form">
                  <div className="form-group">
                    <label className="form-label" htmlFor="payment-status-select">Select Payment Status</label>
                    <div className="dropdown-wrapper">
                      <select
                        id="payment-status-select"
                        className="status-dropdown"
                        value={newPaymentStatus}
                        onChange={e => setNewPaymentStatus(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="1">Paid</option>
                        <option value="0">Unpaid</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={updatePaymentStatus}
                      disabled={!newPaymentStatus || paymentUpdateLoading}
                    >
                      {paymentUpdateLoading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PDF Download Modal */}
          {showPdfModal && (
            <div className="modal-overlay">
              <div className="modal-box">
                <div className="modal-header">
                  <div className="modal-title">PDF Report</div>
                </div>
                <div className="modal-body">
                  <p>PDF download functionality will be implemented here.</p>
                </div>
                <div className="modal-btn-row">
                  <button className="modal-cancel-btn" onClick={closePdfModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Order; 